import {
  ICheckout,
  ILocation,
  IPhoto,
  ITeam,
  IUser,
  Maybe,
  PropertyType
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'

const U_STORAGE = 'RlDm6O5JFYhVMWyEjObYOPGUDwk2'

type LocationProfile = {
  name: string
  pp?: Maybe<IPhoto>
  href: string
  contact: string
  desc?: Maybe<string>
  isTeam: boolean
  hrefWithLocation?: string
  user?: Maybe<IUser>
  isVerified?: Maybe<boolean>
}

export function getLocationProfile(
  location: Maybe<ILocation> | undefined,
  configurationId?: string | null
): LocationProfile {
  if (!location) {
    return {
      name: '',
      pp: undefined,
      href: '',
      contact: '',
      desc: '',
      isTeam: false
    }
  }

  const { propertyType, bussinessProfile, team, user } = location

  const displayLastname = user?.lastname
    ? user?.lastname.length > 2
      ? `${user?.lastname?.charAt(0)}.`
      : user?.lastname
    : ''
  const userDisplayName =
    (user?.firstname ?? user?.userChanges?.firstname ?? 'Usuario Anonimo') +
    ' ' +
    displayLastname

  const backup = {
    name: userDisplayName,
    pp: user?.profilePicture,
    // No user on the listing (no team, no business profile either) → no link;
    // the template rendered `/user/undefined` on /checkoutDetails (2026-10-29).
    href: user?.id ? `${RedirectUrls.User}/${user.id}` : '',
    hrefWithLocation: user?.id
      ? `${RedirectUrls.User}/${user.id}?locationId=${location?.id}&configurationId=${configurationId}`
      : undefined,
    contact: `${RedirectUrls.RentMessages}/recipient/${user?.id}`,
    desc: user?.description,
    isTeam: false
  }

  // Prioritize team info when a team exists
  const teamProfile = team
    ? {
        name: team?.name,
        pp: team?.icon,
        href: `${RedirectUrls.BusinessTeam}/${team?.id}`,
        hrefWithLocation: `${RedirectUrls.BusinessTeam}/${team?.id}?locationId=${location?.id}&configurationId=${configurationId}`,
        contact: `${RedirectUrls.RentMessages}/recipient/team/${team?.id}`,
        desc: team?.description,
        isTeam: true
      }
    : null

  const businessProfile =
    bussinessProfile && user?.id !== U_STORAGE
      ? {
          name: bussinessProfile?.name,
          pp: bussinessProfile?.profilePicture,
          href: `${RedirectUrls.Business}/${location?.id}`,
          hrefWithLocation: `${RedirectUrls.Business}/${location?.id}?configurationId=${configurationId}`,
          contact: `${RedirectUrls.RentMessages}/recipient/${user?.id}`,
          desc: bussinessProfile?.description,
          isTeam: false
        }
      : null

  // Priority: team > business profile > user (for non-residential)
  // For residential: team > user (a residential listing can still belong to a
  // team — an admin creating it on the team's behalf, a family/estate account.
  // Until 2026-08-21 this branch skipped `teamProfile` entirely and always fell
  // through to the individual submitter, which is how team-owned listings ended
  // up labelled with a SpotMe admin's name.)
  const { name, pp, href, hrefWithLocation, contact, desc, isTeam } =
    teamProfile
      ? (teamProfile as LocationProfile)
      : propertyType !== PropertyType.Residential &&
          bussinessProfile &&
          user?.id !== U_STORAGE
        ? (businessProfile as LocationProfile)
        : backup

  const newUser: IUser = {
    ...user,
    firstname: name,
    lastname: '',
    description: desc,
    profilePicture: pp
  } as IUser

  return {
    name,
    pp,
    href,
    hrefWithLocation,
    contact,
    desc,
    user: newUser,
    isVerified: user?.isVerified,
    isTeam
  }
}

// ---------------------------------------------------------------------------
// Host / party display names — TEAM-FIRST. CANONICAL ENTRY POINTS.
//
// A listing created for a team (the common case when a SpotMe admin onboards
// an operator) carries BOTH `teamId`/`team` and an individual `submittedBy`
// user. The team is the landlord; the user is just whoever typed the listing
// in. Every surface that names the host must therefore read the team first and
// treat the individual as a secondary line, never as the label.
//
// Use `getLocationProfile` when you need the full profile (avatar, href,
// contact link) — e.g. the listing-page host masthead. Use the helpers below
// for compact rows, table cells, cards and admin panels where you only need
// text.
//
// `personName` is the human behind the listing. It is null when that human IS
// the primary label (individual host), so callers can render
// `{name}{personName && ` · ${personName}`}` unconditionally.
// ---------------------------------------------------------------------------

export type PartyNames = {
  /** Primary label — the team name whenever the party is a team. */
  name: string
  /** The individual behind the party. Null when the individual IS `name`. */
  personName: string | null
  isTeam: boolean
}

function fullName(user: Maybe<IUser> | undefined): string {
  if (!user) return ''
  return [
    user.firstname ?? user.userChanges?.firstname,
    user.lastname ?? user.userChanges?.lastname
  ]
    .filter(Boolean)
    .join(' ')
    .trim()
}

function toPartyNames(
  team: Maybe<ITeam> | undefined,
  user: Maybe<IUser> | undefined,
  fallback: string
): PartyNames {
  const person = fullName(user)
  if (team?.name) {
    return { name: team.name, personName: person || null, isTeam: true }
  }
  return { name: person || fallback, personName: null, isTeam: false }
}

/**
 * Who the landlord of this listing is, team-first. `fallback` is what to show
 * when neither a team name nor a user name resolves (draft/orphaned listings).
 */
export function getHostNames(
  location: Maybe<ILocation> | undefined,
  fallback = ''
): PartyNames {
  return toPartyNames(location?.team, location?.user, fallback)
}

/**
 * Landlord of a checkout, team-first. Prefers the team stamped on the checkout
 * at creation (`landlordTeam`) and falls back to the listing's current team —
 * they agree for every checkout created after the location was attached to a
 * team, and the location is the better answer for the ones created before.
 */
export function getCheckoutHostNames(
  checkout: Maybe<ICheckout> | undefined,
  fallback = ''
): PartyNames {
  return toPartyNames(
    checkout?.landlordTeam ?? checkout?.location?.team,
    checkout?.landlord ?? checkout?.location?.user,
    fallback
  )
}

/** Renter of a checkout, team-first (B2B renters rent as a company). */
export function getCheckoutRenterNames(
  checkout: Maybe<ICheckout> | undefined,
  fallback = ''
): PartyNames {
  return toPartyNames(checkout?.renterTeam, checkout?.renter, fallback)
}

/**
 * Which side of a checkout a label refers to.
 *
 * Deliberately NOT `CheckoutView` — that enum carries two contradictory
 * meanings in this codebase and picking either one silently mislabels half the
 * surfaces:
 *   - `getCheckoutTabAndView` returns `Rent` when the VIEWER is the renter.
 *   - the `Reservations` tree (empty state, pagination, and both dashboard
 *     pages that feed it) uses `Rent` for the HOST's "Mis rentas" list.
 * Callers must therefore translate their local `CheckoutView` into an explicit
 * side at the call site instead of passing the enum through.
 */
export type CheckoutSide = 'host' | 'renter'

/** Name of one side of a checkout, team-first. */
export function getCheckoutPartyNames(
  checkout: Maybe<ICheckout> | undefined,
  side: CheckoutSide,
  fallback = ''
): PartyNames {
  return side === 'host'
    ? getCheckoutHostNames(checkout, fallback)
    : getCheckoutRenterNames(checkout, fallback)
}

/** Avatar for one side of a checkout — the team icon when it is a team. */
export function getCheckoutPartyPhoto(
  checkout: Maybe<ICheckout> | undefined,
  side: CheckoutSide
): Maybe<IPhoto> | undefined {
  if (side === 'host') {
    const team = checkout?.landlordTeam ?? checkout?.location?.team
    return (
      team?.icon ??
      checkout?.landlord?.profilePicture ??
      checkout?.location?.user?.profilePicture
    )
  }
  return checkout?.renterTeam?.icon ?? checkout?.renter?.profilePicture
}

/**
 * One side of a checkout shaped as an `IUser`, for the components that take a
 * user object (e.g. `<Review owner={...} />`). Mirrors what
 * `getLocationProfile` does: the TEAM's name and icon ride on the user record
 * so the label is team-first, while `id` stays the real person's so profile
 * deep-links keep working.
 */
export function getCheckoutPartyUser(
  checkout: Maybe<ICheckout> | undefined,
  side: CheckoutSide
): Maybe<IUser> | undefined {
  const person =
    side === 'host'
      ? (checkout?.landlord ?? checkout?.location?.user)
      : checkout?.renter
  const { name, isTeam } = getCheckoutPartyNames(checkout, side)
  if (!isTeam) return person
  return {
    ...person,
    firstname: name,
    lastname: '',
    profilePicture: getCheckoutPartyPhoto(checkout, side)
  } as IUser
}
